/**
 * Custom Hook for Email Form Handling
 * Manages form state, validation, and submission
 */

import { useState, useCallback } from 'react';
import { EmailPayload, EmailServiceError, isValidEmail, isValidPhoneNumber } from '../services/emailService';

export interface FormErrors {
  [key: string]: string;
}

export interface UseEmailFormOptions {
  onSubmit: (payload: EmailPayload) => Promise<void>;
}

export interface UseEmailFormReturn {
  isLoading: boolean;
  isSubmitted: boolean;
  showSuccess: boolean;
  errors: FormErrors;
  successMessage: string;
  errorMessage: string;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  resetForm: () => void;
  setErrors: React.Dispatch<React.SetStateAction<FormErrors>>;
  clearMessages: () => void;
  closeSuccessModal: () => void;
}

/**
 * Custom hook for managing email form state and submission
 */
export const useEmailForm = (options: UseEmailFormOptions): UseEmailFormReturn => {
  const { onSubmit } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const resetForm = useCallback(() => {
    setIsLoading(false);
    setIsSubmitted(false);
    setShowSuccess(false);
    setErrors({});
    setSuccessMessage('');
    setErrorMessage('');
  }, []);

  const clearMessages = useCallback(() => {
    setSuccessMessage('');
    setErrorMessage('');
  }, []);

  const closeSuccessModal = useCallback(() => {
    setShowSuccess(false);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      clearMessages();
      setIsLoading(true);
      setErrors({});

      const form = e.currentTarget;

      try {
        const formData = new FormData(e.currentTarget);
        const payload = Object.fromEntries(formData) as any;

        await onSubmit(payload);

        setSuccessMessage('Email sent successfully! I\'ll get back to you soon.');
        setIsSubmitted(true);
        setShowSuccess(true);
        if (form) {
          form.reset();
        }
      } catch (error) {
        setIsSubmitted(false);
        setShowSuccess(false);

        if (error instanceof EmailServiceError) {
          setErrorMessage(
            error.message || 'Failed to send email. Please try again.'
          );
        } else if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unexpected error occurred. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [onSubmit, clearMessages]
  );

  return {
    isLoading,
    isSubmitted,
    showSuccess,
    errors,
    successMessage,
    errorMessage,
    handleSubmit,
    resetForm,
    setErrors,
    clearMessages,
    closeSuccessModal,
  };
};

/**
 * Validates query form fields
 */
export const validateQueryForm = (data: any): FormErrors => {
  const errors: FormErrors = {};

  if (!data.fullName?.trim()) {
    errors.fullName = 'Full name is required';
  }

  if (!data.emailAddress?.trim()) {
    errors.emailAddress = 'Email address is required';
  } else if (!isValidEmail(data.emailAddress)) {
    errors.emailAddress = 'Please enter a valid email address';
  }

  if (!data.phoneNumber?.trim()) {
    errors.phoneNumber = 'Phone number is required';
  } else if (!isValidPhoneNumber(data.phoneNumber)) {
    errors.phoneNumber = 'Please enter a valid phone number';
  }

  if (!data.querry?.trim()) {
    errors.querry = 'Query is required';
  }

  return errors;
};

/**
 * Validates "Get in Touch" form fields
 */
export const validateGetInTouchForm = (data: any): FormErrors => {
  const errors: FormErrors = {};

  if (!data.fullName?.trim()) {
    errors.fullName = 'Full name is required';
  }

  if (!data.email?.trim()) {
    errors.email = 'Email address is required';
  } else if (!isValidEmail(data.email)) {
    errors.email = 'Please enter a valid email address';
  }

  if (!data.subject?.trim()) {
    errors.subject = 'Subject is required';
  }

  if (!data.message?.trim()) {
    errors.message = 'Message is required';
  }

  return errors;
};

/**
 * Validates "Connect" form fields
 */
export const validateConnectForm = (data: any): FormErrors => {
  const errors: FormErrors = {};

  if (!data.senderName?.trim()) {
    errors.senderName = 'Full name is required';
  }

  if (!data.senderEmail?.trim()) {
    errors.senderEmail = 'Email address is required';
  } else if (!isValidEmail(data.senderEmail)) {
    errors.senderEmail = 'Please enter a valid email address';
  }

  if (!data.message?.trim()) {
    errors.message = 'Message is required';
  }

  return errors;
};
